import { CheckoutSchema } from "@/lib/validation/payment";
import { migrateCheckoutStore } from "@/lib/checkout-store-migration";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface ProductCustomization {
  surface?: number;
  height?: string;
  surfaceType?: string;
  montage?: boolean;
  tapis?: boolean;
  jupe?: boolean;
  escalier?: boolean;
}

export interface ProductCustomizations {
  [medusaId: string]: ProductCustomization;
}

type CheckoutState = Partial<CheckoutSchema> & {
  justCompletedOrder: boolean;
  productCustomizations: ProductCustomizations;
  setData: (data: Partial<CheckoutSchema>) => void;
  setJustCompletedOrder: (value: boolean) => void;
  setProductCustomization: (medusaId: string, customization: Partial<ProductCustomization>) => void;
  removeProductCustomization: (medusaId: string) => void;
  getProductCustomization: (medusaId: string) => ProductCustomization | undefined;
  resetFormData: () => void;
};

export const useCheckoutStore = create<CheckoutState>()(
  persist(
    (set, get) => ({
      justCompletedOrder: false,
      productCustomizations: {},

      setData: (data) => set(data),

      setJustCompletedOrder: (value) => set({ justCompletedOrder: value }),

      setProductCustomization: (medusaId, customization) =>
        set((state) => ({
          productCustomizations: {
            ...state.productCustomizations,
            [medusaId]: {
              ...state.productCustomizations[medusaId],
              ...customization,
            },
          },
        })),

      removeProductCustomization: (medusaId) =>
        set((state) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [medusaId]: _, ...rest } = state.productCustomizations;
          return { productCustomizations: rest };
        }),

      getProductCustomization: (medusaId) => get().productCustomizations[medusaId],

      resetFormData: () =>
        set({
          accountType: undefined,
          billingAddress: undefined,
          promoCode: undefined,
          date: undefined,
          deliveryAddress: undefined,
          deliveryDate: undefined,
          wantsDelivery: undefined,
          recoveryDate: undefined,
          email: undefined,
          firstName: undefined,
          wantsToRecoverItems: undefined,
          lastName: undefined,
          message: undefined,
          phone: undefined,
          title: undefined,
          eventType: undefined,
          productCustomizations: {},
        }),
    }),
    {
      name: "checkout-storage",
      storage: createJSONStorage(() => localStorage),
      version: 2,
      migrate: (persistedState) => migrateCheckoutStore(persistedState as Record<string, unknown>) as CheckoutState,
    }
  )
);
